import { parseNumber } from '../utils/text.js';

/** The server's ceiling on a delivery line's quantity and a stock amount
 *  (`lineQuantity` in logistics.schemas, `amount` in inventory.schemas). */
export const MAX_QUANTITY = 1_000_000;

/** Why a typed amount is refused, so a form can say which rule it broke:
 *  not a plain decimal, zero or below, or over `MAX_QUANTITY`. */
export type QuantityIssue = 'not-a-number' | 'not-positive' | 'too-large';

/** A parsed amount against the server's rule. `allowZero` is a sync's count,
 *  which may honestly land on zero ("nothing arrived, nothing left"). */
export function amountIssue(num: number, allowZero = false): QuantityIssue | null {
  if (!Number.isFinite(num)) return 'not-a-number';
  if (allowZero ? num < 0 : num <= 0) return 'not-positive';
  return num > MAX_QUANTITY ? 'too-large' : null;
}

/** A typed delivery quantity's issue, or null when it is fine. Blank is fine:
 *  an unsent draft may not know the amount yet. The server stores a line to
 *  one decimal, so an amount that rounds to 0 (0.04) is not positive either. */
export function quantityIssue(raw: string): QuantityIssue | null {
  if (raw.trim() === '') return null;
  const num = parseNumber(raw);
  return amountIssue(num) ?? (Math.round(num * 10) === 0 ? 'not-positive' : null);
}
